import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useProjectManager } from '@/hooks/useProjectManager';
import {
  LayoutDashboard,
  Clock,
  FolderKanban,
  DollarSign,
  BarChart3,
  Users,
  LogOut,
  Menu,
  X,
  User
} from 'lucide-react';
import { clsx } from 'clsx';
import Button from '@/components/ui/Button';

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasAccessToProjectCosts, isProjectManager } = useProjectManager();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    // Only show dashboard for admin users
    ...(user?.role === 'admin' ? [{
      path: '/dashboard',
      icon: LayoutDashboard,
      label: '仪表板',
    }] : []),
    {
      path: '/work-records',
      icon: Clock,
      label: '工作记录',
    },
    // Only show project management for admin users
    ...(user?.role === 'admin' ? [{
      path: '/projects',
      icon: FolderKanban,
      label: '项目管理',
    }] : []),
    {
      path: '/reports',
      icon: BarChart3,
      label: '统计报表',
    },
  ];

  // Add project cost menu item for users who have access
  if (hasAccessToProjectCosts) {
    // Insert before reports menu item
    const reportsIndex = menuItems.findIndex(item => item.path === '/reports');
    if (reportsIndex !== -1) {
      menuItems.splice(reportsIndex, 0, 
        {
          path: '/project-costs',
          icon: DollarSign,
          label: '项目成本与收入',
        }
      );
    }
  }

  // Add supervisor menu items (only for supervisor, not admin since admin has its own)
  if (user?.role === 'supervisor') {
    menuItems.push({
      path: '/all-logs',
      icon: Clock,
      label: '全局日志',
    });
  }

  // Add manager menu items (for actual project managers, excluding admin since admin has more comprehensive view)
  if (isProjectManager && user?.role !== 'admin') {
    menuItems.push({
      path: '/team-logs',
      icon: Clock,
      label: '团队日志',
    });
  }

  // Add admin-only menu items
  if (user?.role === 'admin') {
    menuItems.push(
      {
        path: '/users',
        icon: Users,
        label: '用户管理',
      },
      {
        path: '/admin/work-logs',
        icon: Clock,
        label: '工作日志',
      }
    );
  }

  const handleMenuClick = (path: string) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-25 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-elevated transform transition-transform duration-200 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      )}>
        <div className="flex flex-col h-full">
          {/* Logo and close button */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-900">项目管理系统</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => handleMenuClick(item.path)}
                  className={clsx(
                    'w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                    isActive
                      ? 'bg-primary-50 text-primary-700 border-r-2 border-primary-500'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  )}
                >
                  <Icon className="h-5 w-5 mr-3" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* User info and logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 mb-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-primary-600 rounded-full flex items-center justify-center">
                  <User className="h-4 w-4 text-white" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {user?.real_name || user?.username}
                </p>
                <p className="text-xs text-gray-500">
                  {user?.role === 'admin' && '管理员'}
                  {user?.role === 'supervisor' && '督导'}
                  {user?.role === 'manager' && '项目经理'}
                  {user?.role === 'employee' && '员工'}
                  {user?.role === 'contractor' && '外包人员'}
                  {user?.role === 'intern' && '实习生'}
                  {user?.role === 'other' && '其他'}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="w-full justify-start"
            >
              <LogOut className="h-4 w-4 mr-2" />
              退出登录
            </Button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1 rounded-md hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">项目管理系统</h1>
            <div className="w-8" /> {/* Spacer for alignment */}
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-auto">
          <div className="p-0 h-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AppLayout;